// @ts-check
import { computed, ref } from 'vue';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  useEnvelopesRoutes,
  useProgressUpdate,
  useUpdateEnvelopeProgress,
} from '@/composables/envelopes';
import i18n from '@/locales';

/**
 * Envelopes creation flow/steps route stuff
 *
 * @param {() => Promise<any>} update
 * @param {import('vue').ComputedRef<boolean>} isValid
 */
export function useEnvelopesStepsNavigation(update, isValid) {
  const router = useRouter();
  const currentRoute = useRoute();

  const { updateEnvelopeProgress } = useUpdateEnvelopeProgress(
    currentRoute.params.fingerprint,
  );

  const { prevRoute, nextRoute, isCreateRoute } = useEnvelopesRoutes();

  const progressUpdate = useProgressUpdate();

  const onBack = computed(() => {
    if (isCreateRoute.value) {
      return () => router.push({ name: 'Envelopes' });
    }

    if (!prevRoute.value) {
      return undefined;
    }

    return () =>
      router.push(prevRoute.value).catch((error) => {
        // Don't throw an error if the navigation was redirected
        if (!isNavigationFailure(error, NavigationFailureType.redirected)) {
          Promise.reject(error);
        }
      });
  });

  const isUpdating = ref(false);

  const onNext = computed(() => {
    if (!nextRoute.value) {
      return undefined;
    }

    return async () => {
      if (!nextRoute.value) {
        return;
      }

      try {
        isUpdating.value = true;
        const data = await update();

        if (progressUpdate.value && currentRoute.params.fingerprint) {
          await updateEnvelopeProgress({
            steps_progress: progressUpdate.value,
          });
        }

        const route = { name: nextRoute.value.name };
        if (isCreateRoute.value && data.fingerprint) {
          route.params = { fingerprint: data.fingerprint };
        }

        await router.push(route).catch((error) => {
          // Don't throw an error if the navigation was redirected
          if (!isNavigationFailure(error, NavigationFailureType.redirected)) {
            Promise.reject(error);
          }
        });
      } catch (e) {
        // errors handled in update
      } finally {
        isUpdating.value = false;
      }
    };
  });

  const next = computed(() => ({
    action: onNext.value,
    busy: isUpdating.value,
    disabled: !isValid.value,
  }));

  const back = computed(() => ({
    action: onBack.value,
    disabled: isUpdating.value,
  }));

  return {
    next,
    back,
  };
}

export function useEnvelopesProgress() {
  const { stepsRoutes, currentIndex, isEditRoute, isCreateRoute } =
    useEnvelopesRoutes();

  const route = useRoute();

  const stepIndex = computed(() =>
    isCreateRoute.value ? 0 : currentIndex.value,
  );

  const steps = computed(() => {
    if (isEditRoute.value) {
      return undefined;
    }

    // Hide the steps progress bar when on the EnvelopesStepAddFields page
    if (route.name === 'EnvelopesStepAddFields') {
      return undefined;
    }

    return stepsRoutes
      .filter((stepRoute) => stepRoute.name !== 'EnvelopesStepAddFields')
      .map((stepRoute, i) => ({
        title: i18n.t(`ui.envelopes.create.stepTitles.${stepRoute.name}`),
        current: stepRoute.name === route.name,
        disabled: i > stepIndex.value,
        clickable: i > stepIndex.value,
      }));
  });

  // Next steps routes, used to calculate which components to keep alive
  const nextStepsRoutes = computed(() => stepsRoutes.slice(currentIndex.value));

  return { steps, stepsRoutes, nextStepsRoutes };
}
